import {Component, computed, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {AttachFileParams, EntityFileType} from "../../../../../../core/types/file.types";
import {DesignBlockData} from "../base-design-block-modal/types/base-design-block.types";
import {defaultDesignBlockData} from "../base-design-block-modal/configs/default-design-block-data";
import {AttachProductCardFileParams, GetFilesParams} from "../../../types/product-card-files.types";
import {BannerSocialFilesStateService} from "./services/banner-social-files-state.service";
import {BannerSocialFilesService} from "./services/banner-social-files.service";
import {BannerSocialShowFileLibraryStateService} from "./services/banner-social-show-file-library-state.service";
import {BannerSocialFilePathStateService} from "./services/banner-social-file-path-state.service";
import {BannerSocialFileData, BannerSocialFilesDialogData} from "./types/banners-social-modal.types";
import {bannersSocialEntityFileTypes, bannersSocialGetFilesParamKeys} from "./configs/banners-social-modal-config";
import {CarouselComponent} from "../../../../../../shared/components/ui/carousel/carousel.component";
import {
  FileUploadCardComponent
} from "../../../../../../shared/components/ui/file-upload-card/file-upload-card.component";
import {BannersSocialHeaderComponent} from "./components/banners-social-header/banners-social-header.component";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {BannersSocialBottomComponent} from "./components/banners-social-bottom/banners-social-bottom.component";
import {
  BannersSocialFileLibraryComponent
} from "./components/banners-social-file-library/banners-social-file-library.component";
import {BannersSocialFileCardComponent} from "./components/banners-social-file-card/banners-social-file-card.component";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-banners-social-modal',
  imports: [
    BaseModalComponent,
    CarouselComponent,
    FileUploadCardComponent,
    BannersSocialHeaderComponent,
    BannersSocialBottomComponent,
    BannersSocialFileLibraryComponent,
    BannersSocialFileCardComponent,
    LoaderComponent
  ],
  templateUrl: './banners-social-modal.component.html',
  styleUrl: './banners-social-modal.component.scss',
  providers: [ EditModeService ]
})
export class BannersSocialModalComponent extends BaseModalComponent {
  public bannerSocialFilesDialogData = inject<BannerSocialFilesDialogData>(MAT_DIALOG_DATA);
  public bannerSocialFilesStateService: BannerSocialFilesStateService = inject(BannerSocialFilesStateService);
  private bannerSocialFilesService: BannerSocialFilesService = inject(BannerSocialFilesService);
  private bannerSocialShowFileLibraryStateService: BannerSocialShowFileLibraryStateService = inject(BannerSocialShowFileLibraryStateService);
  private bannerSocialFilePathStateService: BannerSocialFilePathStateService = inject(BannerSocialFilePathStateService);

  private uploadQueue: Array<AttachFileParams> = [];
  public isShowFileLibrary = computed(() => this.bannerSocialShowFileLibraryStateService.isShowFileLibrary());
  public remainingFileSlots = computed(() => this.bannerSocialFilesStateService.getRemainingFileSlots());
  public modalKey = computed(() => this.bannerSocialFilesDialogData.modalKey);
  public designBlockData = signal<DesignBlockData>(defaultDesignBlockData);
  public bannerSocialEntityFileType = computed<EntityFileType>(() => bannersSocialEntityFileTypes[this.modalKey()] || 'BANNERS');
  public isActiveUploadFile: boolean = false;
  public currentIndex: number = 0;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.modalKey());
    this.bannerSocialFilesStateService.resetBannerSocialFilesState();
    this.bannerSocialShowFileLibraryStateService.resetShowFileLibrary();
  }

  public onIndexChange(index: number): void {
    this.currentIndex = index;
  }

  public uploadFiles(isActiveUploadFile: boolean): void {
    this.isActiveUploadFile = isActiveUploadFile;
  }

  public handleLoading(isLoading: boolean): void {
    this.loadingService.setIsLoadingState(isLoading);
  }

  public async attachFile(attachFileParams: AttachFileParams): Promise<void> {
    this.uploadQueue.push(attachFileParams);

    if (this.uploadQueue.length === 1) {
      this.processUploadQueue();
    }
  }

  public async updateComment(data: DesignBlockData): Promise<void> {
    this.designBlockData.set(data);
  }

  public updateField(file: BannerSocialFileData): void {
    this.bannerSocialFilesStateService.updateBannerSocialFilesState(file);
  }

  public async getFiles(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    const params: GetFilesParams = {
      path: this.bannerSocialFilePathStateService.pathFileState(),
      params: { [bannersSocialGetFilesParamKeys[this.modalKey()]]: this.designBlockData().id }
    };

    await this.bannerSocialFilesService.getFiles(params)
      .then((data) => this.bannerSocialFilesStateService.setBannerSocialFilesState(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(this.modalKey());
    this.bannerSocialFilePathStateService.setPathFileState(this.modalKey());
    this.designBlockData.set(this.bannerSocialFilesDialogData[this.modalKey()] as DesignBlockData);
    await this.getFiles();
  }

  private processUploadQueue(): void {
    if (this.uploadQueue.length === 0) { return; }

    const { parentId, fileId } = this.uploadQueue[0];
    const attachFileParams: AttachProductCardFileParams = {
      path: this.bannerSocialFilePathStateService.pathFileState(),
      body: {
        fileId: fileId,
        [bannersSocialGetFilesParamKeys[this.modalKey()]]: parentId,
        priority: this.bannerSocialFilesStateService.getNextFreePriority()
      }
    };

    this.bannerSocialFilesService.attachFile<BannerSocialFileData>(attachFileParams)
      .then((data) => this.bannerSocialFilesStateService.updateBannerSocialFilesState(data))
      .finally(() => {
        this.uploadQueue.shift();
        this.uploadQueue.length > 0 && this.processUploadQueue();
      });
  }
}
